/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { useLazyQueryWithContext } from '@exo/frontend-common-hooks';
import { gql } from '@exo/frontend-gql-generated-types';
import {
  CartRecommendedProductsQueryVariables,
  CartRecommendedProductsQuery
} from '@exo/frontend-gql-generated-types/graphql';

const GET_CART_RECOMMENDED_PRODUCTS =
  gql(`query CartRecommendedProducts($baseSiteId: ID!, $cartId: ID!, $fields: String, $limit: String, $currency: String, $language: String) {
  cartRecommendedProducts(baseSiteId: $baseSiteId, cartId: $cartId, fields: $fields, limit: $limit, currency: $currency, language: $language) {
    id
    items {
      id
      target {
        EAN
        GTIN
        NDC
        availabilityStatus
        images {
          thumbnail {
             id
            imageType
            format
            url
            altText
            index
          }
          zoom {
             id
            imageType
            format
            url
            altText
            index
          }
          product {
             id
            imageType
            format
            url
            altText
            index
          }
        }
        isPurchasable
        isNewProduct
        manufacturer
        name
        productCode
        promotions {
          id
          type
          value {
            value
            currency
            formattedValue
          }
        }
        stock {
          id
          status
          quantity
          fromData
          stockColor
        }
        summary
        uoms {
          id
          grossWeight
          netWeight
          unit {
            id
            displayValue
            name
            code
          }
          grossWeightUnit {
            id
            displayValue
            name
            code
          }
          netWeightUnit {
            id
            displayValue
            name
            code
          }
        }
        url
        volumePrices {
          id
          price {
            value
            currency
            formattedValue
          }
          minQuantity
          maxQuantity
          formattedPrice
          isCustomerPrice
          uom
        }
        description
        freeGoods {
          id
          isPromotionAvailable
          buyQuantity
          getQuantity
          unitOfFreeProduct
        }
        futureStocks {
          id
          status
          quantity
          fromData
          stockColor
        }
        id
      }
    }
  }
}
`);

export const useCartRecommendedProductsQuery = () => {
  type QueryParametersType = Omit<CartRecommendedProductsQueryVariables, 'baseSiteId'>;

  const [getCartRecommendedProducts, { data, loading }] = useLazyQueryWithContext<
    CartRecommendedProductsQuery,
    QueryParametersType
  >(GET_CART_RECOMMENDED_PRODUCTS);

  return {
    getCartRecommendedProducts: async ({ cartId, fields, limit, language }: QueryParametersType) => {
      return getCartRecommendedProducts({
        variables: { cartId, fields, limit, language }
      });
    },
    data: data?.cartRecommendedProducts.items,
    loading
  };
};
